// ─── API ──────────────────────────────────────────────────────────────────────

export const WAITLIST_API_ENDPOINTS = Object.freeze({
  CONFIGS: "admin/waitlist/configs",
  FORM_OPTIONS: "admin/waitlist/form-options"
});

/** Interval (ms) at which the dispatch progress modal polls for updates. */
export const DISPATCH_POLL_INTERVAL_MS = 3000;

/** Public website base URL (mirrors the CP-digest blog preview convention). */
export const PUBLIC_WEBSITE_BASE_URL = "https://www.tle-eliminators.com";

// ─── Domain enums (mirror the backend) ────────────────────────────────────────

/**
 * Mongoose discriminator key on WaitlistConfig — mirrors WAITLIST_TYPE on the backend.
 */
export const WAITLIST_TYPES = Object.freeze({
  DISCOUNT_ALLOWLIST: "DISCOUNT_ALLOWLIST",
  USER_ACCESS_GRANT: "USER_ACCESS_GRANT",
  GENERAL_WAITLIST: "GENERAL_WAITLIST"
});

/** Types whose members are processed by a dispatch. GENERAL_WAITLIST completes on join, so it has no dispatch/skip concept. */
export const DISPATCHABLE_WAITLIST_TYPES = [
  WAITLIST_TYPES.DISCOUNT_ALLOWLIST,
  WAITLIST_TYPES.USER_ACCESS_GRANT
];

/**
 * Name of the type-specific target field on WaitlistConfig, keyed by type.
 * GENERAL_WAITLIST has none.
 */
export const TYPE_TARGET_FIELD_NAME = Object.freeze({
  [WAITLIST_TYPES.DISCOUNT_ALLOWLIST]: "displayCouponCode",
  [WAITLIST_TYPES.USER_ACCESS_GRANT]: "accessKey"
});

/** Mirrors WAITLIST_ENTRY_STATUS on the backend. */
export const ENTRY_STATUS = Object.freeze({
  PENDING: "PENDING",
  COMPLETED: "COMPLETED",
  SKIPPED: "SKIPPED"
});

/** Mirrors WAITLIST_DISPATCH_STATE on the backend. */
export const DISPATCH_STATES = Object.freeze({
  QUEUED: "QUEUED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED"
});

/** Dispatch states that block a new dispatch from being triggered. */
export const ACTIVE_DISPATCH_STATES = [DISPATCH_STATES.QUEUED, DISPATCH_STATES.IN_PROGRESS];

// ─── Display copy ─────────────────────────────────────────────────────────────

export const WAITLIST_TYPE_LABELS = Object.freeze({
  [WAITLIST_TYPES.DISCOUNT_ALLOWLIST]: "Discount Allowlist",
  [WAITLIST_TYPES.USER_ACCESS_GRANT]: "User Access Grant",
  [WAITLIST_TYPES.GENERAL_WAITLIST]: "General Waitlist"
});

/** One/two-line explanation of what each waitlist type does, surfaced in the create/edit form. */
export const WAITLIST_TYPE_DESCRIPTIONS = Object.freeze({
  [WAITLIST_TYPES.DISCOUNT_ALLOWLIST]:
    "Links the waitlist to a private discount coupon; dispatched members become eligible to redeem it at checkout. Use for early-bird sales.",
  [WAITLIST_TYPES.USER_ACCESS_GRANT]:
    "Grants an accessConfig flag (e.g. prime access) to members' user accounts on dispatch. Use for staged feature rollouts.",
  [WAITLIST_TYPES.GENERAL_WAITLIST]:
    "A plain email-collection waitlist. Members join and are completed immediately — no dispatch. Its dedicated mailing list makes the audience reusable for campaigns."
});

/** Mirrors WAITLIST_ENTRY_SKIP_REASON on the backend; fallback labels if form-options aren't loaded. */
export const SKIP_REASON_LABELS = Object.freeze({
  NO_USER_ACCOUNT: "No user account"
});

// ─── Styling ──────────────────────────────────────────────────────────────────

export const WAITLIST_TYPE_TAG_COLORS = Object.freeze({
  [WAITLIST_TYPES.DISCOUNT_ALLOWLIST]: "magenta",
  [WAITLIST_TYPES.USER_ACCESS_GRANT]: "purple",
  [WAITLIST_TYPES.GENERAL_WAITLIST]: "blue"
});

export const ENTRY_STATUS_TAG_COLORS = Object.freeze({
  [ENTRY_STATUS.PENDING]: "gold",
  [ENTRY_STATUS.COMPLETED]: "green",
  [ENTRY_STATUS.SKIPPED]: "default"
});

export const DISPATCH_STATE_TAG_COLORS = Object.freeze({
  [DISPATCH_STATES.QUEUED]: "default",
  [DISPATCH_STATES.IN_PROGRESS]: "processing",
  [DISPATCH_STATES.COMPLETED]: "green",
  [DISPATCH_STATES.FAILED]: "red"
});

/** Columns of the per-waitlist entry tally, in display order, with their "non-zero" colour. */
export const ENTRY_COUNT_FIELDS = [
  { key: "total", label: "Total" },
  { key: "pending", label: "Pending", color: "#D4A017" },
  { key: "completed", label: "Done", color: "#52C41A" },
  { key: "skipped", label: "Skipped", color: "#8C8C8C" }
];
